import { BackButton } from "@4uhub/lib4uhub";
import GridOnRoundedIcon from "@mui/icons-material/GridOnRounded";
import PrintRoundedIcon from "@mui/icons-material/PrintRounded";
import { Box, Button, Divider } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import GroupedReportResultTable from "./GroupedReportResultTable";
import ReportPrintStyles from "./ReportPrintStyles";
import ReportPrintableTable from "./ReportPrintableTable";
import ReportResultHeader from "./ReportResultHeader";
import ReportResultLoading from "./ReportResultLoading";
import ReportResultMetadata from "./ReportResultMetadata";
import {
  buildReportExecutionSearchParams,
  parseReportExecutionSearchParams,
  REPORT_EXECUTION_TRANSLATION_PATH,
} from "./reportExecutionUtils";
import { exportReportToExcel } from "./reportExcelExport";
import {
  actionsSx,
  documentSx,
  exportActionsSx,
  pageSx,
} from "./reportResultStyles";
import { useReportResult } from "../../../../hooks/useReportResult";

interface IReportResultPageProps {
  reportLogoSrc?: string;
}

const DEFAULT_REPORT_LOGO_SRC = "/4uhubApps/4uSpots.png";

const ReportResultPage: React.FC<IReportResultPageProps> = ({
  reportLogoSrc = DEFAULT_REPORT_LOGO_SRC,
}) => {
  const { i18n, t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isExporting, setIsExporting] = useState(false);
  const {
    columns,
    filterItems,
    generatedAt,
    generatedBy,
    generationDate,
    isLoading,
    pageTitle,
    periodicity,
    reportCode,
    reportResult,
    rows,
    selectedMonth,
  } = useReportResult();

  const filterSearch = buildReportExecutionSearchParams(
    parseReportExecutionSearchParams(searchParams),
  ).toString();

  const metadataItems = selectedMonth
    ? filterItems.map((item, index) =>
        index === 0
          ? {
              ...item,
              value: `${item.value} (${selectedMonth.charAt(0).toLocaleUpperCase()}${selectedMonth.slice(1)})`,
            }
          : item,
      )
    : filterItems;

  const hasDataRows = rows.some((row) => row.isTotal !== true);

  const fileBaseName = useMemo(() => {
    const pad = (value: number) => String(value).padStart(2, "0");
    const date = `${generationDate.getFullYear()}-${pad(generationDate.getMonth() + 1)}-${pad(generationDate.getDate())}`;
    const time = `${pad(generationDate.getHours())}h${pad(generationDate.getMinutes())}m${pad(generationDate.getSeconds())}s`;
    const safeTitle = pageTitle.replace(/[\\/:*?"<>|]/g, "").trim();
    const codePrefix =
      reportCode !== undefined && reportCode !== null ? `${reportCode} - ` : "";

    return `${codePrefix}${safeTitle} - ${date} ${time}`;
  }, [generationDate, pageTitle, reportCode]);

  useEffect(() => {
    let originalTitle = document.title;

    const beforePrintHandler = () => {
      originalTitle = document.title;
      document.title = fileBaseName;
    };
    const afterPrintHandler = () => {
      document.title = originalTitle;
    };

    window.addEventListener("beforeprint", beforePrintHandler);
    window.addEventListener("afterprint", afterPrintHandler);

    return () => {
      window.removeEventListener("beforeprint", beforePrintHandler);
      window.removeEventListener("afterprint", afterPrintHandler);
    };
  }, [fileBaseName]);

  const exportExcelHandler = async () => {
    setIsExporting(true);

    try {
      await exportReportToExcel({
        title: pageTitle,
        subtitle: `${t(REPORT_EXECUTION_TRANSLATION_PATH + "code")}: ${reportCode ?? "-"} · ${t(
          REPORT_EXECUTION_TRANSLATION_PATH + "header.generatedLine",
          {
            date: generatedAt,
            user: generatedBy,
            interpolation: { escapeValue: false },
          },
        )}`,
        sheetName: t(REPORT_EXECUTION_TRANSLATION_PATH + "reportLabel"),
        fileName: `${fileBaseName}.xlsx`,
        filterItems: metadataItems,
        columns,
        rows,
        periodicity,
        grouping: reportResult.grouping,
        t,
        language: i18n.language,
      });
    } catch (error) {
      console.error(error);
    } finally {
      setIsExporting(false);
    }
  };

  useEffect(() => {
    document.body.classList.add("report-print-mode");

    return () => {
      document.body.classList.remove("report-print-mode");
    };
  }, []);

  return (
    <Box sx={pageSx}>
      <ReportPrintStyles />

      <Box sx={actionsSx}>
        <BackButton
          onClick={() =>
            navigate(
              {
                pathname: "..",
                search: filterSearch ? `?${filterSearch}` : "",
              },
              { relative: "path", replace: true },
            )
          }
        />
      </Box>

      <Box sx={exportActionsSx}>
        <Button
          size="small"
          startIcon={<PrintRoundedIcon />}
          disabled={isLoading}
          onClick={() => window.print()}
        >
          {t(REPORT_EXECUTION_TRANSLATION_PATH + "actions.print")}
        </Button>
        <Divider flexItem orientation="vertical" />
        <Button
          size="small"
          startIcon={<GridOnRoundedIcon />}
          disabled={isLoading || isExporting || !hasDataRows}
          onClick={exportExcelHandler}
        >
          {t(REPORT_EXECUTION_TRANSLATION_PATH + "actions.exportExcel")}
        </Button>
      </Box>

      <Box component="article" sx={documentSx}>
        <ReportResultHeader
          generatedAt={generatedAt}
          generatedBy={generatedBy}
          logoSrc={reportLogoSrc}
          reportCode={reportCode}
          title={pageTitle}
        />
        <ReportResultMetadata items={metadataItems} />

        {isLoading ? (
          <ReportResultLoading
            message={t(REPORT_EXECUTION_TRANSLATION_PATH + "loading")}
          />
        ) : (
          <>
            {reportResult.grouping ? (
              <GroupedReportResultTable
                columns={columns}
                emptyMessage={
                  t(
                    REPORT_EXECUTION_TRANSLATION_PATH + "noResultData",
                  ) as string
                }
                grouping={reportResult.grouping}
                periodicity={periodicity}
                rows={rows}
              />
            ) : (
              <ReportPrintableTable
                columns={columns}
                emptyMessage={
                  t(
                    REPORT_EXECUTION_TRANSLATION_PATH + "noResultData",
                  ) as string
                }
                periodicity={periodicity}
                rows={rows}
              />
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

export default ReportResultPage;
